import { Fragment } from "react"
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router"
import { ArrowLeft } from "@/components/icons"
import { TopBarBackButton } from "@/components/topbar/TopBarBackButton"
import { AppLayout } from "@/components/AppLayout"
import { DesktopPage } from "@/components/desktop/DesktopPage"
import { DesktopPageHeader } from "@/components/desktop/DesktopPageHeader"
import { useIsDesktop } from "@/components/ui/use-mobile"
import { Card } from "@/components/ui/card"
import { MenuItem } from "@/components/ui/menu-item"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { DetailSkeleton } from "@/components/ui/skeleton-presets"
import {
  useCrewGetRegionalSettings,
  useCrewUpdateRegionalSettings,
} from "@/lib/hooks/crew"

export const Route = createFileRoute("/_auth/profile/settings/regional")({
  component: RouteComponent,
})

type TemperatureUnit = "Fahrenheit" | "Celsius"

const TEMPERATURE_OPTIONS: { value: TemperatureUnit; label: string }[] = [
  { value: "Fahrenheit", label: "Fahrenheit" },
  { value: "Celsius", label: "Celsius" },
]

// Flight-time display on the Home flight card: UTC and Zulu label the UTC
// time ("17:00 UTC" / "17:00 ZU"); Local Time shows each airport's local
// time ("17:00 LT").
type TimezoneDisplay = "UTC" | "Zulu" | "Local"

const TIMEZONE_OPTIONS: { value: TimezoneDisplay; label: string }[] = [
  { value: "UTC", label: "UTC" },
  { value: "Zulu", label: "Zulu (ZU)" },
  { value: "Local", label: "Local Time" },
]

function RouteComponent() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { data: settings, isLoading } = useCrewGetRegionalSettings()
  const { mutate: updateSettings } = useCrewUpdateRegionalSettings()

  const body = (
    <>
      {isLoading ? (
        <DetailSkeleton rows={4} />
      ) : (
        <div className="flex flex-col gap-6">
          <div>
            <p className="text-sm font-semibold text-content-primary-bold mb-4 px-1">
              Temperature Unit
            </p>
            <Card className="flex flex-col gap-2 w-full px-2">
              <RadioGroup
                value={settings?.temperatureUnit}
                onValueChange={(value) =>
                  updateSettings({ temperatureUnit: value as TemperatureUnit })
                }
                className="flex flex-col gap-2"
              >
                {TEMPERATURE_OPTIONS.map((option, index) => (
                  <Fragment key={option.value}>
                    {index > 0 && <hr className="border-border-L1" />}
                    <MenuItem
                      rightIcon={<RadioGroupItem value={option.value} />}
                      size="large"
                      onClick={() =>
                        updateSettings({ temperatureUnit: option.value })
                      }
                    >
                      {option.label}
                    </MenuItem>
                  </Fragment>
                ))}
              </RadioGroup>
            </Card>
          </div>

          <div>
            <p className="text-sm font-semibold text-content-primary-bold mb-4 px-1">
              Timezone
            </p>
            <Card className="flex flex-col gap-2 w-full px-2">
              <RadioGroup
                value={settings?.timezoneDisplay}
                onValueChange={(value) =>
                  updateSettings({ timezoneDisplay: value as TimezoneDisplay })
                }
                className="flex flex-col gap-2"
              >
                {TIMEZONE_OPTIONS.map((option, index) => (
                  <Fragment key={option.value}>
                    {index > 0 && <hr className="border-border-L1" />}
                    <MenuItem
                      rightIcon={<RadioGroupItem value={option.value} />}
                      size="large"
                      onClick={() =>
                        updateSettings({ timezoneDisplay: option.value })
                      }
                    >
                      {option.label}
                    </MenuItem>
                  </Fragment>
                ))}
              </RadioGroup>
            </Card>
          </div>

        </div>
      )}
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Regional"
          breadcrumb={
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate({ to: "/profile/settings" })}
                className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
                aria-label="Back to settings"
              >
                <ArrowLeft className="size-4" />
              </button>
              <span>
                <Link to="/profile" className="hover:underline">
                  Profile
                </Link>
                <span className="text-content-primary-subtle"> / </span>
                <Link to="/profile/settings" className="hover:underline">
                  Settings
                </Link>
                <span className="text-content-primary-subtle"> / Regional</span>
              </span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Regional" />}>
      {body}
    </AppLayout>
  )
}
